import { CreateBlock, ResolveBlock } from '@bechtel/client-blocks';
import { ErrorBoundary, SuspenseLoader, useAppUser, useConfig, WeldMilestoneType } from '@bechtel/client-common';
import {
  useCancelFitupRequest,
  WeldActionErrorExt,
  WeldClaim,
  WeldSendBackSideSheet,
} from '@bechtel/client-weld-common';
import { Box, Set, SetDirection, SetSpacing, Text, useSideSheetState } from '@bechtel/gds';
import {
  CommodityMilestone,
  CommodityStatusCode,
  CommodityType,
  MilestoneActivity,
  MilestoneStatusCode,
  PersonaType,
  UnitOfMeasure,
  Weld,
} from '@bechtel/types';
import { t } from '@lingui/macro';
import { addDays, isBefore, parseISO } from 'date-fns';
import { filter, find, get, includes, some } from 'lodash';
import React, { FC, Suspense, useState } from 'react';
import { CommodityDetail } from '~/components/organisms/commodity-detail';
import { CommodityContext } from '~/detail/commodities/context';
import { useWorkPackage } from '~/detail/context';
import DocumentManager from '~/detail/document-manager/internals';
import { useWorkPackagePermissions } from '~/hooks';
import CommodityAction from '../action';
import { CommodityMilestoneListStyled, MilestoneError } from '../milestones';
import { ReclaimSideSheet } from '../milestones/weld-flange-connections/reclaim/reclaim-sidesheet';
import { FitupMilestoneMock } from '../milestones/weld-flange-connections/types';
import { WeldMilestone } from '../milestones/weld-flange-connections/weld-milestone';
import { useWeldStatusText } from './hooks';
import { CommodityInfoStyled, WeldCommodityItemStyled } from './styled';

interface CommodityItemProps {
  commodity: Weld;
  type: CommodityType;
  collapsed?: boolean;
  isUnitOfMeasureHour: boolean;
  collapseMe?: () => void;
}

interface SidesheetContentProps {
  weldDetails: Weld;
  milestoneData: CommodityMilestone;
  setMilestoneData: (milestone: CommodityMilestone | null) => void;
}

const SidesheetContent: FC<SidesheetContentProps> = ({ weldDetails, milestoneData, setMilestoneData }) => {
  const { id, assignedCrewMembers, facilities } = useWorkPackage();
  const { features } = useConfig();
  const isFitupEnabled = features.canApproveFitupInspection ? features.canApproveFitupInspection : false;

  let showWeldClaimSidesheet: boolean = false;

  if (isFitupEnabled) {
    showWeldClaimSidesheet =
      includes(
        [MilestoneStatusCode.UNPLANNED, MilestoneStatusCode.REJECTED, MilestoneStatusCode.PLANNED],
        milestoneData?.currentStatus?.codeI18n
      ) &&
      includes(
        [CommodityStatusCode.READY_TO_WELD, CommodityStatusCode.VT_SENT_BACK, CommodityStatusCode.FITUP_INSPECTION],
        weldDetails?.status?.status
      );
  } else {
    showWeldClaimSidesheet = includes(
      [MilestoneStatusCode.UNPLANNED, MilestoneStatusCode.REJECTED, MilestoneStatusCode.PLANNED],
      milestoneData?.currentStatus?.codeI18n
    );
  }

  if (showWeldClaimSidesheet) {
    return (
      <ErrorBoundary>
        <WeldClaim
          weld={weldDetails}
          isReclaim={milestoneData?.currentStatus?.codeI18n == MilestoneStatusCode.REJECTED}
          close={() => setMilestoneData(null)}
        />
      </ErrorBoundary>
    );
  }

  if (milestoneData?.currentStatus?.codeI18n == MilestoneStatusCode.BLOCKED) {
    return (
      <ResolveBlock
        workPackageId={id}
        workPackageTag={weldDetails.tag}
        facilities={facilities}
        assignedCrewMembers={assignedCrewMembers}
        weldCommodityAssignees={weldDetails.commodityAssignees}
        commodityTag={weldDetails?.tag}
        commodityTypeName={weldDetails?.type.name}
        milestone={milestoneData}
        onClose={() => setMilestoneData(null)}
      />
    );
  }

  if (
    weldDetails?.status?.status === CommodityStatusCode.FITUP_INSPECTION ||
    weldDetails?.status?.status === CommodityStatusCode.CLAIMED
  ) {
    return (
      <ErrorBoundary>
        <WeldSendBackSideSheet
          weld={weldDetails}
          currentMilestone={milestoneData}
          close={() => {
            setMilestoneData(null);
          }}
        />
      </ErrorBoundary>
    );
  }

  if (weldDetails?.status?.status === CommodityStatusCode.SENT_BACK_TO_PIPING) {
    return (
      <ErrorBoundary>
        <ReclaimSideSheet milestone={milestoneData} closeModal={() => setMilestoneData(null)} />
      </ErrorBoundary>
    );
  }

  return null;
};

export const WeldComponent: React.FC<CommodityItemProps> = ({ commodity, type, collapsed, isUnitOfMeasureHour, collapseMe }) => {
  const { user } = useAppUser();
  const { features } = useConfig();
  const { tag: workpackageTag, assignedCrewMembers: workOwners, id: workPackageId, facilities } = useWorkPackage();
  const [cancelFitupRequestError, setCancelFitupRequestError] = useState<WeldActionErrorExt | null>();
  const [milestoneData, setMilestoneData] = useState<CommodityMilestone | null>(null);
  const {
    visible: visibleCreateBlock,
    open: openCreateBlockSideSheet,
    close: closeCreateBlockSideSheet,
  } = useSideSheetState(false);

  const {
    visible: visibleCommodityDetail,
    open: openCommodityDetailSideSheet,
    close: closeCommodityDetailSideSheet,
  } = useSideSheetState(false);

  const {
    visible: showDocumentManager,
    open: openDocumentManager,
    close: closeDocumentManager,
  } = useSideSheetState(false);
  const { canCreateFitupRequest } = useWorkPackagePermissions(workOwners);
  const isFitupEnabled = features.canApproveFitupInspection ? features.canApproveFitupInspection : false;
  const commodityAssignee = commodity.commodityAssignees?.length
    ? find(commodity.commodityAssignees, (item) => {
        return item.personaType == PersonaType.FOREMAN;
      })
    : null;
  const assignedWeldForeman = commodityAssignee && commodityAssignee.assignee;
  const fitupMilestoneName = WeldMilestoneType.FITUP;
  const fitupRequest = commodity?.fitupRequest;
  const excludeNotApplicableandCanceledMilestone = (milestones: CommodityMilestone[]) => {
    return filter(
      milestones,
      (milestone) =>
        ![MilestoneStatusCode.NOT_APPLICABLE, MilestoneStatusCode.CANCELED].includes(milestone.currentStatus.codeI18n)
    );
  };

  let milestones = excludeNotApplicableandCanceledMilestone(commodity?.milestones);

  const showCancelFitupRequest = () => {
    const fitupMilestoneCurrentStatus = find(commodity?.milestones, (item) => {
      return item.name.toUpperCase() === fitupMilestoneName;
    })?.currentStatus;

    const parsedFitupRequestCreatedAt = fitupMilestoneCurrentStatus?.createdAt
      ? parseISO(fitupMilestoneCurrentStatus?.createdAt)
      : undefined;

    return (
      canCreateFitupRequest &&
      fitupMilestoneCurrentStatus?.activity === MilestoneActivity.REQUESTED &&
      user.id == fitupMilestoneCurrentStatus?.createdBy?.id &&
      parsedFitupRequestCreatedAt &&
      isBefore(new Date(addDays(new Date(), -2).toISOString()), new Date(parsedFitupRequestCreatedAt))
    );
  };

  const hasFitupMilestone = some(milestones, (item) => {
    return item.name.toUpperCase() === fitupMilestoneName;
  });

  if (!hasFitupMilestone && isFitupEnabled) {
    milestones = [FitupMilestoneMock, ...milestones];
  }
  const onCompleted = (data) => {
    const error = get(data, 'cancelFitupRequest.error');
    if (error) {
      setCancelFitupRequestError(error);
    } else {
      setCancelFitupRequestError(null);
      close();
    }
  };
  const onError = (error) => {
    setCancelFitupRequestError(error);
  };
  const [cancelFitupRequest, { loading: cancelFitupRequestloading }] = useCancelFitupRequest(onCompleted, onError, [
    'commodityStatsSummary',
  ]);

  const onCancelFitupRequest = async () => {
    const fitupMilestoneId = find(commodity.milestones, (item) => {
      return item.name.toUpperCase() === fitupMilestoneName;
    })?.id;
    if (!fitupMilestoneId) return;
    await cancelFitupRequest({
      variables: { input: { commodityMilestoneId: fitupMilestoneId } },
    });
  };

  return (
    <>
      {cancelFitupRequestError && <MilestoneError milestoneError={cancelFitupRequestError} />}
      <CommodityContext id={commodity.id} tag={commodity.tag} typeName={type.name} typeId={type.id}>
        <WeldCommodityItemStyled>
          <CommodityInfoStyled>
            <Set direction={SetDirection.COL} spacing={SetSpacing.COMPACT}>
              <Box alignItems={{ sm: 'center' }} pt={{ sm: '200' }}>
                <Text weight="600" color="grey.1000" size="400" family="graphik">
                  {commodity.tag}
                </Text>
                <Box ml={{ sm: '400' }}>
                  <Text color={'grey.600'} size={'400'}>
                    {`(${commodity.earnedHours || '0'}/${commodity.totalEarnableHours || '0'} ${t`Hours`})`}
                  </Text>
                </Box>
              </Box>
              <Text color={'grey.600'} size={'300'}>
                {useWeldStatusText(commodity)}
              </Text>
            </Set>
            <Box alignItems={{ sm: 'center' }} pt={{ sm: '400' }}>
              {showCancelFitupRequest() && (
                <CommodityAction
                  icon="xmark"
                  iconText={t`Cancel Request`}
                  onClick={onCancelFitupRequest}
                  iconFillColor="red.500"
                  textFontColor="red.500"
                  disabled={cancelFitupRequestloading}
                  loading={cancelFitupRequestloading}
                />
              )}
              <CommodityAction icon="list" iconText={t`Details`} onClick={openCommodityDetailSideSheet} />
              {/* <CommodityAction icon="cube" iconText={t`3D`} /> */}
              <CommodityAction icon="ruler-combined" iconText={t`ISO`} onClick={openDocumentManager} />
              <CommodityAction icon="exclamation-triangle" iconText={t`Block`} onClick={openCreateBlockSideSheet} />
              <CommodityAction
                icon="compress-alt"
                iconText={collapsed ? t`Expand` : t`Collapse`}
                onClick={() => collapseMe && collapseMe()}
              />
            </Box>
          </CommodityInfoStyled>
          <CommodityMilestoneListStyled milestonesCount={milestones.length}>
            {milestones.length &&
              milestones.map((milestone) => {
                const isFitupMilestone = milestone?.name?.toUpperCase() == fitupMilestoneName;
                if (isFitupMilestone && !isFitupEnabled) return;
                return (
                  <ErrorBoundary key={milestone?.id}>
                    <WeldMilestone
                      hidden={collapsed}
                      milestone={milestone}
                      disabled={milestone?.currentStatus?.codeI18n === MilestoneStatusCode.NOT_APPLICABLE}
                      isFitupMilestone={isFitupMilestone && hasFitupMilestone ? false : isFitupMilestone}
                      hasFitupRequest={isFitupMilestone && hasFitupMilestone}
                      weld={commodity}
                      assignee={fitupRequest?.installationCrewMember ?? assignedWeldForeman ?? undefined}
                      setMilestoneData={setMilestoneData}
                      assignReassignVisible={true}
                      actionLoading={cancelFitupRequestloading}
                      isUnitOfMeasureHour={isUnitOfMeasureHour}
                    />
                  </ErrorBoundary>
                );
              })}
          </CommodityMilestoneListStyled>

          {visibleCommodityDetail && (
            <ErrorBoundary>
              <Suspense fallback={<SuspenseLoader></SuspenseLoader>}>
                <CommodityDetail
                  commodityId={commodity.id}
                  commodityName={type.name}
                  commodityTag={commodity.tag}
                  workPackageTag={workpackageTag}
                  close={closeCommodityDetailSideSheet}
                />
              </Suspense>
            </ErrorBoundary>
          )}

          {showDocumentManager && (
            <ErrorBoundary>
              <Suspense fallback={<SuspenseLoader></SuspenseLoader>}>
                <DocumentManager
                  workPackageId={workPackageId}
                  onClose={closeDocumentManager}
                  selectedTag={commodity.tag}
                  selectedDrawingNo={commodity.isoDocument?.documentNumber}
                />
              </Suspense>
            </ErrorBoundary>
          )}

          {visibleCreateBlock && (
            <ErrorBoundary>
              <Suspense fallback={<SuspenseLoader></SuspenseLoader>}>
                <CreateBlock
                  workPackageId={workPackageId}
                  workPackageTag={workpackageTag}
                  assignedCrewMembers={workOwners}
                  weldCommodityAssignees={commodity?.commodityAssignees}
                  facilities={facilities ? facilities : []}
                  close={closeCreateBlockSideSheet}
                  multiBlock={false}
                  // @ts-ignore commodity conversion to commodity array
                  commodities={[commodity]}
                />
              </Suspense>
            </ErrorBoundary>
          )}
          {milestoneData?.id && (
            <SidesheetContent
              weldDetails={commodity}
              milestoneData={milestoneData}
              setMilestoneData={setMilestoneData}
            />
          )}
        </WeldCommodityItemStyled>
      </CommodityContext>
    </>
  );
};
